import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ActivityLogger from '../utils/activityLogger';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const EmailModal = ({ document, user, onClose, onEmailSent }) => {
  const [formData, setFormData] = useState({
    recipient_email: '',
    subject: '',
    message: '',
    template_id: '',
    enable_reminders: false
  });

  const [templates, setTemplates] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [processedMessage, setProcessedMessage] = useState('');
  const [userSignature, setUserSignature] = useState('');

  useEffect(() => {
    fetchTemplates();
    fetchContacts();
    generateUserSignature();
    setDefaultSubject();
  }, [document]);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/email-templates`);
      setTemplates(response.data.filter(t => t.document_type === document.type));
    } catch (error) {
      console.error('Erreur lors du chargement des templates:', error);
    }
  };

  const fetchContacts = async () => {
    try {
      // Fetch contacts from the centralized directory
      const response = await axios.get(`${API}/contacts`);
      setContacts(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des contacts:', error);
    }
  };

  const generateUserSignature = () => {
    if (user) {
      const signature = `${user.full_name}
${user.role || 'Commercial'}
Baobab Connect
${user.email}
+225 0500163737`;
      setUserSignature(signature);
    }
  };

  const setDefaultSubject = () => {
    let subject = '';
    switch (document.type) {
      case 'devis':
        subject = `Votre devis – Réf. ${document.reference}`;
        break;
      case 'bon_commande':
        subject = `Bon de commande – Réf. ${document.reference}`;
        break;
      case 'facture':
        subject = `Facture à régler – Réf. ${document.reference}`;
        break;
      default:
        subject = `Document – Réf. ${document.reference}`;
    }
    setFormData(prev => ({ ...prev, subject }));
  };

  const getDefaultTemplate = () => {
    const variables = {
      contact_name: '{{contact_name}}',
      document_reference: document.reference,
      montant_total: document.total_ttc.toLocaleString() + ' FCFA',
      date_creation: new Date(document.date).toLocaleDateString('fr-FR'),
      client_name: document.client.name,
      user_signature: userSignature
    };

    let template = '';
    switch (document.type) {
      case 'devis':
        template = `Bonjour {{contact_name}},

Veuillez trouver ci-joint notre devis pour votre projet.

Référence : ${variables.document_reference}
Montant TTC : ${variables.montant_total}
Date : ${variables.date_creation}

Ce devis est valable 30 jours. N'hésitez pas à nous contacter pour toute question ou précision.

Cordialement,
${variables.user_signature}`;
        break;
      case 'bon_commande':
        template = `Bonjour {{contact_name}},

Veuillez trouver ci-joint le bon de commande pour votre commande.

Référence : ${variables.document_reference}
Montant TTC : ${variables.montant_total}
Date : ${variables.date_creation}

Nous procéderons à l'exécution de votre commande selon les termes convenus.

Cordialement,
${variables.user_signature}`;
        break;
      case 'facture':
        template = `Bonjour {{contact_name}},

Veuillez trouver ci-joint la facture relative à votre commande.

Référence : ${variables.document_reference}
Montant TTC : ${variables.montant_total}
Date : ${variables.date_creation}

Merci de procéder au règlement dans les délais convenus.

Cordialement,
${variables.user_signature}`;
        break;
      default:
        template = `Bonjour {{contact_name}},

Veuillez trouver ci-joint le document demandé.

Référence : ${variables.document_reference}
Date : ${variables.date_creation}

Cordialement,
${variables.user_signature}`;
    }
    return template;
  };

  const handleTemplateSelect = async (templateId) => {
    if (!templateId) {
      setFormData(prev => ({ 
        ...prev, 
        template_id: '', 
        message: getDefaultTemplate() 
      }));
      return;
    }

    try {
      const template = templates.find(t => t.id === templateId);
      if (template) {
        setFormData(prev => ({
          ...prev,
          template_id: templateId,
          subject: template.subject,
          message: template.body
        }));
      }
    } catch (error) {
      console.error('Erreur lors de la sélection du template:', error);
    }
  };

  const processTemplate = (template, contactName = '') => {
    const variables = {
      contact_name: contactName || 'Cher(e) client(e)',
      document_reference: document.reference,
      montant_total: document.total_ttc.toLocaleString() + ' FCFA',
      date_creation: new Date(document.date).toLocaleDateString('fr-FR'),
      client_name: document.client.name,
      user_signature: userSignature,
      lieu_depart: document.lieu_depart || 'À définir',
      lieu_arrivee: document.lieu_arrivee || 'À définir',
      date_expiration: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString('fr-FR'),
      date_echeance: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString('fr-FR')
    };

    let processed = template;
    Object.entries(variables).forEach(([key, value]) => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      processed = processed.replace(regex, value);
    });

    return processed;
  };

  const handlePreview = () => {
    const contactName = contacts.find(c => c.email === formData.recipient_email)?.nom || '';
    const processed = processTemplate(formData.message, contactName);
    setProcessedMessage(processed);
    setPreviewMode(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const contactName = contacts.find(c => c.email === formData.recipient_email)?.nom || '';
      const processedMessage = processTemplate(formData.message, contactName);
      const processedSubject = processTemplate(formData.subject, contactName);

      const emailData = {
        document_id: document.id,
        recipient_email: formData.recipient_email,
        subject: processedSubject,
        message: processedMessage,
        template_id: formData.template_id || null,
        enable_reminders: formData.enable_reminders
      };

      await axios.post(`${API}/documents/send-email`, emailData);
      
      // Log the email sending activity
      try {
        await ActivityLogger.documentSent(user, document, formData.recipient_email, formData.template_id);
      } catch (logError) {
        console.warn('Failed to log document email send:', logError);
      }
      
      alert('Email envoyé avec succès !');
      onEmailSent();
    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
      alert('Erreur lors de l\'envoi de l\'email');
    } finally {
      setLoading(false);
    }
  };

  // Initialize message with default template
  useEffect(() => {
    if (!formData.message) {
      setFormData(prev => ({ ...prev, message: getDefaultTemplate() }));
    }
  }, [document, userSignature]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto m-4">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">
              ✉️ Envoyer {document.type === 'devis' ? 'le devis' : 
                         document.type === 'bon_commande' ? 'le bon de commande' : 
                         'la facture'} par email
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-xl"
            >
              ✕
            </button>
          </div>
          <p className="text-gray-600 mt-1">
            Document : {document.reference} - {document.client.name}
          </p>
        </div>

        {!previewMode ? (
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Destinataire */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Destinataire *
              </label>
              <div className="space-y-3">
                {/* Email personnalisé - Prioritaire */}
                <div>
                  <input
                    type="email"
                    placeholder="Saisir l'email du destinataire"
                    value={formData.recipient_email}
                    onChange={(e) => setFormData(prev => ({ ...prev, recipient_email: e.target.value }))}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary focus:border-baobab-primary"
                    required
                  />
                </div>
                
                {/* Ou sélection depuis les contacts */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">ou choisir un contact existant</span>
                  </div>
                </div>
                
                <select
                  value={contacts.find(c => c.email === formData.recipient_email) ? formData.recipient_email : ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      setFormData(prev => ({ ...prev, recipient_email: e.target.value }));
                    }
                  }}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary focus:border-baobab-primary bg-gray-50"
                >
                  <option value="">Sélectionner un contact...</option>
                  {contacts.map(contact => (
                    <option key={contact.id} value={contact.email}>
                      {contact.nom} {contact.prenom} ({contact.email}) - {contact.company_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Template */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Template d'email
              </label>
              <select
                value={formData.template_id}
                onChange={(e) => handleTemplateSelect(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary focus:border-baobab-primary"
              >
                <option value="">Template par défaut</option>
                {templates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Objet */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Objet *
              </label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary focus:border-baobab-primary"
                required
              />
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message *
              </label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary focus:border-baobab-primary"
                rows={12}
                required
              />
              <div className="mt-2 text-sm text-gray-500">
                <p>Variables disponibles :</p>
                <div className="flex flex-wrap gap-2 mt-1">
                  {[
                    '{{contact_name}}',
                    '{{document_reference}}',
                    '{{montant_total}}',
                    '{{date_creation}}',
                    '{{client_name}}',
                    '{{user_signature}}'
                  ].map(variable => (
                    <span key={variable} className="bg-gray-100 px-2 py-1 rounded text-xs">
                      {variable}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Options */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-3">Options</h4>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.enable_reminders}
                    onChange={(e) => setFormData(prev => ({ ...prev, enable_reminders: e.target.checked }))}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">
                    Activer les relances automatiques
                  </span>
                </label>
              </div>
            </div>

            {/* Signature */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Signature automatique</h4>
              <div className="text-sm text-gray-700 whitespace-pre-line">
                {userSignature}
              </div>
            </div>

            {/* Boutons */}
            <div className="flex gap-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-600 text-white py-3 px-4 rounded-lg hover:bg-gray-700 transition-colors"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handlePreview}
                className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors"
              >
                👁️ Prévisualiser
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-baobab-primary text-white py-3 px-4 rounded-lg hover:bg-baobab-dark transition-colors disabled:opacity-50"
              >
                {loading ? 'Envoi...' : '📧 Envoyer'}
              </button>
            </div>
          </form>
        ) : (
          <div className="p-6">
            <div className="mb-4">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Prévisualisation</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p><strong>À:</strong> {formData.recipient_email}</p>
                <p><strong>Objet:</strong> {processTemplate(formData.subject, contacts.find(c => c.email === formData.recipient_email)?.nom)}</p>
              </div>
            </div>
            
            <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
              <div className="whitespace-pre-line text-gray-800">
                {processedMessage}
              </div>
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => setPreviewMode(false)}
                className="flex-1 bg-gray-600 text-white py-3 px-4 rounded-lg hover:bg-gray-700 transition-colors"
              >
                ← Retour
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 bg-baobab-primary text-white py-3 px-4 rounded-lg hover:bg-baobab-dark transition-colors disabled:opacity-50"
              >
                {loading ? 'Envoi...' : '📧 Envoyer'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailModal;